import { PropertyMediaKind, MeliPublicationTarget } from 'src/api/generated';
import type { CatalogProperty } from 'src/api/generated/models/CatalogProperty';
import type { MeliPublishValidationIssue } from 'src/api/generated/models/MeliPublishValidationIssue';
import type { PropertyPortalPublication } from 'src/api/generated/models/PropertyPortalPublication';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';
import { isRecord } from 'src/modules/shared/utils/types.util';
import type { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { getPropertyMediaFileIdsByKinds } from './property-media.util';

export const isPortalTarget = (value: string): value is MeliPublicationTarget =>
  Object.values<string>(MeliPublicationTarget).includes(value);

const getPublicationConfigRecord = (
  publication: PropertyPortalPublication | null | undefined
): Record<string, unknown> | null =>
  isRecord(publication?.config) ? publication.config : null;

export const getPublicationPortalTarget = (
  publication: PropertyPortalPublication | null | undefined
): MeliPublicationTarget => {
  const target = getPublicationConfigRecord(publication)?.portalTarget;
  return typeof target === 'string' && isPortalTarget(target)
    ? target
    : MeliPublicationTarget.MELI;
};

export const getDraftIssueVariant = (
  issues: MeliPublishValidationIssue[]
): 'success' | 'warning' => (issues.length === 0 ? 'success' : 'warning');

export const getPublicationPictureFileIds = (
  publication: PropertyPortalPublication | null | undefined,
  propertyPictureFileIds: string[] | null | undefined
): string[] => {
  const pictureFileIds =
    getPublicationConfigRecord(publication)?.pictureFileIds;
  const publicationPictureFileIds = Array.isArray(pictureFileIds)
    ? pictureFileIds.filter(
        (fileId): fileId is string =>
          typeof fileId === 'string' && fileId.trim().length > 0
      )
    : null;

  if (publicationPictureFileIds && publicationPictureFileIds.length > 0) {
    return publicationPictureFileIds;
  }

  return propertyPictureFileIds ?? [];
};

export const getMeliListingTypeLabel = (
  listingTypeId: string,
  t: ReturnType<typeof useI18n>['t']
): string => {
  const listingTypeTranslationKeyById: Partial<Record<string, TranslationKey>> =
    {
      free: 'catalog.properties.meli.listingTypes.free',
      gold_premium: 'catalog.properties.meli.listingTypes.goldPremium',
      gold: 'catalog.properties.meli.listingTypes.gold',
      silver: 'catalog.properties.meli.listingTypes.silver',
    };

  const translationKey = listingTypeTranslationKeyById[listingTypeId];
  if (translationKey !== undefined) {
    return t(translationKey);
  }

  switch (listingTypeId) {
    default:
      return listingTypeId.trim().length > 0
        ? listingTypeId
        : t('catalog.properties.meli.form.listingType.default');
  }
};

export const getPropertyImageFileIds = (
  media: CatalogProperty['media']
): string[] => getPropertyMediaFileIdsByKinds(media, [PropertyMediaKind.IMAGE]);
